import {ConfigService, RunConfigService, events, util} from '@nuskin/ns-util';
import UserService from './userService.js';
import VGDistInfoService from './vgDistInfoService.js';

import axios from 'axios';

/**
 * The Profile Service provides methods for accessing and updating the
 * the users profile.
 * @namespace ProfileService
 */
export default {
    getOrderHistory,
    getOrderDetails,
    getProfileImage,
    getAccountInfo,
    parseOrderDetails,
    updateAccountInfo,
    getVGGreeting,
    updateProfilePhoto,
    updatePreferredName,
    getCountryAndLanguage,
    getLanguageList,
    updatePassword,
    getAvailableBanksForMarket,
    getAvailableBankBranches,
    getBankInfo,
    hasBankInfo,
    updateBankInfo,
    updatePerson,
    createAccountInfo,
    emailNotification,
    sendVerificationEmail,
    validateEmail,
    updatePasswordProxy,
    getHUTaxInfo
};

//=================================================================//
//
//  PUBLIC FUNCTIONS
//
//=================================================================//

const countriesUsingAtlas = 'US, CA, AU, NZ, SG, MY, TH, PH, HK';
const atlasServiceUrl = '/eft-service/api/v2/';
const atlasBankListEndPoint = 'banks/{countryCode}.json';
const atlastBankBranchListEndPoint = 'banks/{countryCode}/{bankCode}.json';
const atlasRetrieveBankInfoUrl = RunConfigService.getBaseUrl() + atlasServiceUrl + atlasBankListEndPoint;
const atlasRetrieveBankBranchInfoUrl = RunConfigService.getBaseUrl() + atlasServiceUrl + atlastBankBranchListEndPoint;
const atlasInfoServiceEndPoint = RunConfigService.getBaseUrl() + atlasServiceUrl + 'bank-info/{accountId}.json?eid={eid}';
const atlasEligibleServiceEndPoint = RunConfigService.getBaseUrl() + atlasServiceUrl + 'eligible/{accountId}.json?eid={eid}';
const updatePasswordEndPoint = RunConfigService.getBaseUrl() + '/account-util/api/v1/account/{accountId}/credentials';
const accountServiceEndPoint = RunConfigService.getBaseUrl() + '/customer/api/v1/account/{accountId}?eid={eid}';
const accountServiceExtendedEndPoint = RunConfigService.getBaseUrl() + '/account-service/api/v1/account/extended/{accountId}';
const accountServicePostEndPoint = RunConfigService.getBaseUrl() + '/customer/api/v1/account';
const personServiceEndPoint = RunConfigService.getBaseUrl() + '/customer/api/v1/person/{personId}';
const emailServiceUrl = RunConfigService.getBaseUrl() + '/esb-util/api/v1/message?locale={locale}';
const countryAndLanguageTranslationURL = RunConfigService.getBaseUrl() + '/content/localization/countryandlanguagetranslation.service.{langCode}.json';

const emailVerifyBase = RunConfigService.getEnvironmentCode() === 'prod' ? 'https://api.cloud.nuskin.com/GigyaProcessor/v1' : 'https://testapi.cloud.nuskin.com/GigyaProcessor/v1';


async function getBankInfoDetails(isEligible) {
    let url = atlasInfoServiceEndPoint.replace('{accountId}', UserService.getUser().id).replace('{eid}',UserService.getUser().eid);
    if(isEligible) {
        url = atlasEligibleServiceEndPoint.replace('{accountId}', UserService.getUser().id).replace('{eid}',UserService.getUser().eid);
    }
    return await axios.request({
        method: 'get',
        url: url,
        headers: getHeaders(),
        responseType: 'json'
    });
}

async function emailNotification(template, email, locale) {
    let url=emailServiceUrl.replace('{locale}', locale);
    let notifyData = {
        'messageID': template,
        'sendToEmail': email,
        'messageData': {
            'NAME': UserService.getUser().localName
        }
    };
    return await axios.request({
        method: 'post',
        url: url,
        data: notifyData,
        headers: getHeaders(),
        responseType: 'json'
    });
}

async function updateBankInfo(bankInfo) {

    let bankData = {
        "registeredName":bankInfo.accountName,
        "accountNumber":bankInfo.accountNumber,
        "accountType":"N",
        "distributor":{
            "countryCode":UserService.getUser().countryCd,
            "distributorId":UserService.getUser().id
        },
        "systemId":"CPM",
        "userId":UserService.getUser().id,
        "bank":{
            "number":bankInfo.routingNumber,
            "branches":[{}]
        }
    };

    if(bankInfo.branchNumber !== undefined && bankInfo.branchNumber !== null && bankInfo.branchNumber.length > 0) {
        bankData.bank.branches[0].name = bankInfo.branchNumber;
    }
    if(bankInfo.accountType !== undefined && bankInfo.accountType !== null && bankInfo.accountType.length > 0) {
        bankData.accountType = bankInfo.accountType;
    }

    let url = atlasInfoServiceEndPoint.replace('{accountId}', UserService.getUser().id).replace('{eid}',UserService.getUser().eid);
    return await axios.request({
        method: 'post',
        url: url,
        data: bankData,
        headers: getHeaders(),
        responseType: 'json'
    });
}

async function hasBankInfo() {
    let bankInfo = await getBankInfoDetails(true);
    return !bankInfo.data.eligible;
}

async function getBankInfo() {
    let countryCode = UserService.getUser().countryCd;
    let bankInfo = {
        'accountNumber': '',
        'accountType': '',
        'routingNumber': '',
        'bankName': '',
        'nameOnAccount': ''
    };
    let bankResponse = await getBankInfoDetails();
    bankInfo.accountNumber = bankResponse.data.accountNumber;
    bankInfo.nameOnAccount = bankResponse.data.registeredName;
    if(countryCode == 'CA' || countryCode == 'NZ') {
        bankInfo.routingNumber = bankResponse.data.bank.branches[0].number;
        bankInfo.bankName = bankResponse.data.bank.name;
    } else if(countryCode == 'US' || countryCode == 'AU') {
        bankInfo.routingNumber = bankResponse.data.bank.number;
        bankInfo.accountType = bankResponse.data.accountType;
    }
    return bankInfo;
}

async function getAvailableBanksForMarket(countryCode) {
    let bankInfo = await retrieveAvailableBanksForMarket(countryCode);
    return bankInfo.data.banks;
}

async function updatePerson(personInfo) {
    try {

        let url = personServiceEndPoint.replace('{personId}', UserService.getUser().personId);
        return await axios.request({
            method: 'put',
            url: url,
            data: personInfo,
            headers: getHeaders(),
            responseType: 'json'
        });
    } catch(e) {
        console.error("updatePassword exception: " , e);
    }
}

async function updatePassword(credentialObj,isUsername) {
    let userInfo = {
        'sapId':UserService.getUser().id,
        'saveLoginIDasContactEmail':false
    };
    try {
        if(isUsername) {
            userInfo.loginID = credentialObj.loginId;
        } else {
            userInfo.password = credentialObj.currentPassword
            userInfo.newPassword = credentialObj.newPassword;
        }
    } catch(e) {
        console.error("updatePassword exception: " , e);
    }

    let url = updatePasswordEndPoint.replace('{accountId}', UserService.getUser().id);
    return await axios.request({
        method: 'put',
        url: url,
        data: userInfo,
        headers: getHeaders(),
        responseType: 'json'
    });

}

async function updatePasswordProxy(credentialObj) {
    let userInfo = {
        'eventType': 'PASSWORD_CHANGE',
        'eventSource': 'PROFILE_VUE_COMPONENT',
        'sapID': UserService.getUser().id,
        'saveLoginIDasContactEmail': false
    };
    userInfo.password = credentialObj.currentPassword
    userInfo.newPassword = credentialObj.newPassword;

    let config = ConfigService.getMarketConfig();

    return await axios.request({
        method: 'put',
        url:  config.awsUrl + '/account-util-proxy/v1/login-account',
        data: userInfo,
        headers: getHeadersWithAuth(),
        responseType: 'json'
    });

}

async function retrieveAvailableBanksForMarket(countryCode) {
    let url = atlasRetrieveBankInfoUrl.replace('{countryCode}', countryCode);

    return await axios.request({
        method: 'get',
        url: url,
        headers: getHeaders(),
        responseType: 'json'
    });
}

async function getAvailableBankBranches(countryCode,bankCode) {
    let url = atlasRetrieveBankBranchInfoUrl.replace('{countryCode}', countryCode).replace('{bankCode}', bankCode);

    return await axios.request({
        method: 'get',
        url: url,
        headers: getHeaders(),
        responseType: 'json'
    });
}

async function getAccountInfo() {
    try {
        let url = accountServiceEndPoint.replace('{accountId}', UserService.getUser().id).replace('{eid}',UserService.getUser().eid) + '&includeInviter=true';

        return await axios.request({
            method: 'get',
            url: url,
            headers: getHeaders(),
            responseType: 'json'
        })
    } catch (e) {
        console.error("vgDistInfoService exception: " , e);
    }
}


async function createAccountInfo(userInfo) {
    return await updateAccountInfo(userInfo,true);
}


async function updateAccountInfo(userInfo,isPost) {
    try {

        let url = '';
        let headers = {};
        let methodValue = 'put';
        if(isPost) {
            methodValue = 'post';
            url = accountServicePostEndPoint;
            headers = getNonAuthHeaders();
        } else {
            url = accountServiceEndPoint.replace('{accountId}', UserService.getUser().id).replace('{eid}',UserService.getUser().eid);
            headers = getHeaders();
        }

        return await axios.request({
            method: methodValue,
            url: url,
            data: userInfo,
            headers: headers,
            responseType: 'json'
        });
    } catch (e) {
        console.error("updateAccountInfo exception: " , e);
    }
}

async function getHUTaxInfo() {
    try {
        let url = accountServiceExtendedEndPoint.replace('{accountId}', UserService.getUser().id);

        let extended = await axios.request({
            method: 'get',
            url: url,
            headers: getHeaders(),
            responseType: 'json'
        });
        let huTaxInfo = {
            'LOGIN': '',
            'PASS': '',
            'XML_SIG': '',
            'XML_EXCH': ''
        }
        extended.data.BeReadResponse.CountrySpecifics.forEach((item) => {
            huTaxInfo[item.Key] = item.Value;
        });
        return Promise.resolve(huTaxInfo);
    } catch (e) {
        console.error("vgDistInfoService exception: " , e);
    }

}

async function getProfileImage() {
    let response = await VGDistInfoService.getVGDistInfo();

    return response.data.photoURL;
}


async function getVGGreeting(){
    let url = RunConfigService.getBaseUrl() +'/vgservice/rest/dist-greeting/'+UserService.getUser().id + '?eid=' + UserService.getUser().eid;
    console.log("greeting URL ",url);
    let headers = getHeaders();
    headers.client = 'staticAuth';
    return await axios.request({
        method: 'get',
        url: url,
        headers: headers,
        responseType: 'json'
    });
}

async function updatePreferredName(newName){
    let url = RunConfigService.getBaseUrl() +'/vgservice/rest/dist-greeting/'+UserService.getUser().id + '?eid=' + UserService.getUser().eid + '&alias=' + newName;
    let headers = getHeaders();
    headers.client = 'staticAuth';
    return await axios.request({
        method: 'post',
        url: url,
        headers: headers,
        responseType: 'json'
    });
}

async function updateProfilePhoto(image){
    let s3url = 'https://nuskin-images-test.s3.amazonaws.com/'
    let s3key =  await axios.request({
        method: 'post',
        url: s3url,
        data: image,
        headers: getHeaders(),
        responseType: 'json'
    });
    console.log("uploaded", s3key);
}

async function getCountryAndLanguage(langCode) {
    let url = countryAndLanguageTranslationURL.replace('{langCode}', langCode);

    let headers = getHeaders();

    return await axios.request({
        method: 'get',
        url: url,
        headers: headers,
        responseType: 'json'
    });
}

async function getLanguageList (langCode, country) {
    let langRes = await getCountryAndLanguage(langCode);

    let languageList = [];

    if (country) {
        let languageOptions = langRes.data[country].languages;

        /*This switch case is a temporary fix for ACCT-4899*/
        Object.keys(languageOptions).forEach((key) => {
            let language = {text: '', value: ''};
            switch (key) {
                case 'in':
                    language.value = 'id';
                    break;
                case 'iw':
                    language.value = 'he';
                    break;
                default:
                    language.value = key;
                    break;
            }
            language.text = languageOptions[key];
            languageList.push(language);

        });
    }
    return Promise.resolve(languageList);
}

async function getOrderDetails(orderNumber, language, market) {
    try {
        let eid = UserService.getUser().eid;

        let orderDetailsServiceEndPoint = RunConfigService.getBaseUrl() + '/shop-service/api/v2/order/'
            + orderNumber + '?cntryCd=' + market + '&locale=' + language + '_' + market + '&eid=' + eid;


        return await axios.request({
            method: 'get',
            url: orderDetailsServiceEndPoint,
            headers: getHeaders(),
            responseType: 'json'
        });

    } catch (e) {
        console.error("vgDistInfoService exception: " , e);
    }
}

/**
 * Send an email verification
 */

async function sendVerificationEmail() {
    try {

        let url = emailVerifyBase + '/sendVerificationEmail'
        return await axios.request({
            method: 'post',
            url: url,
            data: {eid:UserService.getUser().eid},
            headers: getHeadersWithAuth(),
            responseType: 'json'
        });
    } catch(e) {
        console.error("sendVerificationEmail exception: " , e);
    }
}

/**
 * Validate the email through Gigya
 * @param {string} uid
 */

async function validateEmail(uid) {
    let config;
    try {
        config = ConfigService.getMarketConfig();
    } catch(e) {
        // config = userDefinedConfig;
    }
    try {
        let url = emailVerifyBase + '/validateEmail?uuid='+uid;
        return await axios.request({
            method: 'get',
            url: url,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'client_id': config.checkout.clientId,
                'client': 'aem'
            },
            responseType: 'json'
        });
    } catch(e) {
        console.error("validateEmail exception: " , e);
    }
}

/**
 * Retrieves the user's order History.
 * Results are preferentially retrieved from the session storage cache.
 * @param {Object} [options] - options:
 * @param {boolean} [options.doAtp = false] - Requests updated ATP information from the server
 * @param {boolean} [options.forceRefresh = false] - Forces a request from the server instead of from session storage cache
 * @returns
 * @memberof ProfileService
 * @public
 */
async function getOrderHistory(months) {
    let orderInfo = {};

    let searchParam = getTodaysDate(months);

    const historyServiceEndPoint = RunConfigService.getBaseUrl() + '/shop-service/api/v2/order/list/{accountId}?beginDate='
        + searchParam + '&cntryCd=' + getCountry() + '&eid=' + UserService.getUser().eid;

    let accountId = UserService.getUser().id;

    let url = historyServiceEndPoint.replace('{accountId}', accountId);


    return await axios.request({
        method: 'get',
        url: url,
        headers: getHeaders(),
        responseType: 'json'
    });

}






//=================================================================//
//
//  PRIVATE FUNCTIONS
//
//=================================================================//


function getTodaysDate(range) {
    let today = new Date();
    today.setMonth(today.getMonth() - range);
    let dd = today.getDate();
    if (dd < 10) {
        dd = dd.toString();
        dd = '0' + dd;
    } else {
        dd = dd.toString();
    }
    let mm = today.getMonth() + 1;
    if (mm < 10) {
        mm = mm.toString();
        mm = '0' + mm;
    } else {
        mm = mm.toString();
    }
    let yyyy = today.getFullYear();
    yyyy = yyyy.toString();

    let beginDate = yyyy + mm + dd;
    return beginDate;
}


function getCountry() {
    return RunConfigService.getRunConfig().country;
}


/**
 * Returns headers needed for vgService VG-Dist-Info service call
 * @returns {{Accept: string, Content-Type: string, client_id: string, client_secret: string, client: string}}
 * @private
 */
function getNonAuthHeaders(userDefinedConfig) {
    let config;
    try {
        config = ConfigService.getMarketConfig();
    } catch(e) {
        config = userDefinedConfig;
    }

    return {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'client_id': config.checkout.clientId,
        'client_secret': config.checkout.clientSecret,
        'client': 'aem'
    };
}

/**
 * Returns headers needed for vgService VG-Dist-Info service call
 * @returns {{Accept: string, Content-Type: string, client_id: string, client_secret: string, client: string}}
 * @private
 */
function getHeaders(userDefinedConfig) {
    let config;
    try {
        config = ConfigService.getMarketConfig();
    } catch(e) {
        config = userDefinedConfig;
    }

    return {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'client_id': config.checkout.clientId,
        'client_secret': config.checkout.clientSecret,
        'client': 'aem',
        'eid': UserService.getUser().eid
    };
}

/**
 * Returns headers needed for emailVerification
 * @returns {{Accept: string, Content-Type: string, client_id: string, client_secret: string, client: string, Authoization: string}}
 * @private
 */
function getHeadersWithAuth(userDefinedConfig) {
    let config;
    try {
        config = ConfigService.getMarketConfig();
    } catch(e) {
        config = userDefinedConfig;
    }

    return {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'client_id': config.checkout.clientId,
        'client_secret': config.checkout.clientSecret,
        'client': 'aem',
        'Authorization': 'JWT ' + UserService.getUser().eid
    };
}




// ---------------------------------------------
//
//        Define all private methods.
//
// ---------------------------------------------

function canAddLineItem(lineItemDetail) {
    return !(doesNodeExist(lineItemDetail.LineItem) && doesNodeExist(lineItemDetail.LineItem.ParentID) && parseInt(lineItemDetail.LineItem.ParentID) !== 0);
}

function parseLineItem(lineItemDetail){
    return {
        getDescription: function () {
            return lineItemDetail.Name;
        },
        getQty: function () {
            return lineItemDetail.AvailableQuantity;
        },
        getPoints: function () {
            return lineItemDetail.Points;
        },
        getPrice: function () {
            // We devide by the quantity to get the item price.
            return lineItemDetail.Price / lineItemDetail.AvailableQuantity;
        },
        getPsv: function () {
            return lineItemDetail.PSV;
        },
        getTotal: function () {
            return lineItemDetail.Price;
        },
        getSKU: function() {
            return lineItemDetail.LineItem.SKU;
        }
    };
}

function parseOrderDetails(orderDetailJSONData) {
    let salesOrderDetail    = orderDetailJSONData.data.SalesOrderResponse.SalesOrderDetail;

    let orderTotals         = doesNodeExist(salesOrderDetail) ? salesOrderDetail.OrderTotals : undefined;
    let shipping            = doesNodeExist(salesOrderDetail) ? salesOrderDetail.Shipping : undefined;

    let shippingParty       = doesNodeExist(shipping) ? shipping.ShippingParty : [];
    let shippingMethod      = doesNodeExist(shipping) ? shipping.ShippingMethod : undefined;

    let custom              = doesNodeExist(salesOrderDetail) ? salesOrderDetail.Custom : undefined;

    let shippingItems = undefined;
    let payments = undefined;
    let tracking = undefined;

    function getShippingItem(shippingParty, itemType, callback){
        let shippingItem = "---";

        if (doesNodeExist(shippingParty)){
            if (shippingParty.length !== undefined){
                for (let i=0;i<shippingParty.length;i++){
                    if (shippingParty[i].Type == itemType){
                        shippingItem = callback(shippingParty[i]);
                        break;
                    }
                }
            }
            else{
                if (shippingParty.Type == itemType){
                    shippingItem = callback(shippingParty);
                }
            }
        }

        return shippingItem;
    }

    function getShipAddName(shippingParty){
        let shippingAddressName = "---";

        if(doesNodeExist(shippingParty.ShippingAddress) && doesNodeExist(shippingParty.ShippingAddress.Name)){
            shippingAddressName = shippingParty.ShippingAddress.Name;
        }

        return shippingAddressName;
    }

    function getShipAddLine1(shippingParty){
        let shippingAddressLine1 = "";

        if(doesNodeExist(shippingParty.ShippingAddress) &&
            doesNodeExist(shippingParty.ShippingAddress.Address) &&
            doesNodeExist(shippingParty.ShippingAddress.Address.Street1)){
            shippingAddressLine1 = shippingParty.ShippingAddress.Address.Street1;
        }

        return shippingAddressLine1;
    }

    function getShipAddLine2(shippingParty){
        let shippingAddressLine2 = "";

        if(doesNodeExist(shippingParty.ShippingAddress) &&
            doesNodeExist(shippingParty.ShippingAddress.Address) &&
            doesNodeExist(shippingParty.ShippingAddress.Address.Street2)){
            shippingAddressLine2 = shippingParty.ShippingAddress.Address.Street2;
        }

        return shippingAddressLine2;
    }

    function getShipInstructions(shippingParty){
        let shippingAddressInstructions = "";

        if (doesNodeExist(shippingParty.ShippingAddress) && doesNodeExist(shippingParty.ShippingAddress.Instructions)){
            shippingAddressInstructions = shippingParty.ShippingAddress.Instructions;
        }

        return shippingAddressInstructions;
    }

    function getShipAddLine3(shippingParty){
        let shippingAddressLine3 = "";

        if (doesNodeExist(shippingParty.ShippingAddress) && doesNodeExist(shippingParty.ShippingAddress.Address)){
            //City
            if (doesNodeExist(shippingParty.ShippingAddress.Address.City)){
                shippingAddressLine3 += shippingParty.ShippingAddress.Address.City;
                if (shippingAddressLine3.length > 0){
                    shippingAddressLine3 += ', ';
                }
            }
            //State (US & CA)
            if (doesNodeExist(shippingParty.ShippingAddress.Address.Region)){
                shippingAddressLine3 += shippingParty.ShippingAddress.Address.Region;
            }
            //Zip
            if (doesNodeExist(shippingParty.ShippingAddress.Address.PostalCode)){
                if (shippingAddressLine3.length > 0){
                    shippingAddressLine3 += ', ';
                }
                shippingAddressLine3 += shippingParty.ShippingAddress.Address.PostalCode;
            }
        }

        return shippingAddressLine3;
    }

    function getShipAddLine3Japan(shippingParty){
        let shippingAddressLine3Japan = "";

        if (doesNodeExist(shippingParty.ShippingAddress) && doesNodeExist(shippingParty.ShippingAddress.Address)){
            if (doesNodeExist(shippingParty.ShippingAddress.Address.PostalCode) && shippingParty.ShippingAddress.Address.PostalCode.length > 0){
                shippingAddressLine3Japan = shippingParty.ShippingAddress.Address.PostalCode;
            }
        }

        return shippingAddressLine3Japan;
    }

    var orderDetailsObj = {
        getOrderId: function(){
            return doesNodeExist(salesOrderDetail) ? salesOrderDetail.OrderID : "---";
        },
        getOrderStatus: function(){
            let status;

            if (doesNodeExist(salesOrderDetail)){
                status = salesOrderDetail.Status;
                if (status !== undefined && status !== null && status.length > 0){
                    status = status.toUpperCase();
                }
            }

            return status;
        },
        getCurrencyCode: function(){
            return doesNodeExist(salesOrderDetail) ? salesOrderDetail.Currency : "";
        },
        getCountryCode: function(){
            return doesNodeExist(salesOrderDetail) ? salesOrderDetail.CountryCode : "";
        },
        getUserId: function(){
            return doesNodeExist(salesOrderDetail) ? salesOrderDetail.SoldToMemberID : "---";
        },
        getOrderDate: function(){
            let date = doesNodeExist(salesOrderDetail) ? salesOrderDetail.Date : "";
            date = new Date(date);
            return date.toLocaleDateString();
        },
        getOrderChannel: function(){
            let orderChannel;

            if (doesNodeExist(salesOrderDetail)){
                orderChannel = salesOrderDetail.Category;
                if (orderChannel !== undefined && orderChannel !== null && orderChannel.length > 0){
                    orderChannel = orderChannel.toUpperCase();
                }
            }

            return orderChannel;
        },
        getBuyerName: function(){
            return orderDetailsObj.getShippingAddressName();
        },
        getBuyerEmail: function(){
            let callBack = function(shippingParty){
                return getContactItem(shippingParty, 'EMAIL', getContactItemValue);
            };
            return getShippingItem(shippingParty, "SOLD_TO", callBack);
        },
        getBuyerPhone: function(){
            let callBack = function(shippingParty){
                let phone = getContactItem(shippingParty, 'MOBILEPHONE', getContactItemValue);

                if (phone == '---'){
                    phone = getContactItem(shippingParty, 'HOMEPHONE', getContactItemValue);
                }

                return phone;
            };
            return getShippingItem(shippingParty, "SOLD_TO", callBack);
        },
        getFacilitatorId: function(){
            let facilitatorId = getShippingItem(shippingParty, "FACILITATOR", getShippingMemberID);
            if (facilitatorId == "---"){
                // Default to the user id
                facilitatorId = orderDetailsObj.getUserId();
            }
            return facilitatorId;
        },
        getFacilitatorEmail: function(){
            let callBack = function(shippingParty){
                return getContactItem(shippingParty, 'EMAIL', getContactItemValue);
            };
            return getShippingItem(shippingParty, "FACILITATOR", callBack);
        },
        getFacilitatorPhone: function(){
            let callBack = function(shippingParty){
                let phone = getContactItem(shippingParty, 'MOBILEPHONE', getContactItemValue);

                if (phone == '---'){
                    phone = getContactItem(shippingParty, 'HOMEPHONE', getContactItemValue);
                }

                return phone;
            };
            return getShippingItem(shippingParty, "FACILITATOR", callBack);
        },
        getConfirmationEmail: function(){
            let email = orderDetailsObj.getFacilitatorEmail();

            if (email == "---"){
                email = orderDetailsObj.getBuyerEmail();
            }

            return email;
        },
        getConfirmationPhone: function(){
            let phone = orderDetailsObj.getFacilitatorPhone();

            if (phone == "---"){
                phone = orderDetailsObj.getBuyerPhone();
            }

            return phone;
        },
        getShippingItems: function() {
            if (shippingItems === undefined) {
                shippingItems = [];
                let lineItemDetails = doesNodeExist(salesOrderDetail) ? salesOrderDetail.LineItemDetails : [];

                if (doesNodeExist(lineItemDetails)) {
                    // Handle the array
                    if (lineItemDetails.length !== undefined) {
                        for (let i = 0; i < lineItemDetails.length; i++) {
                            // We only add line items that have a ParentID with a value of "000000".
                            if (canAddLineItem(lineItemDetails[i])){
                                shippingItems.push(parseLineItem(lineItemDetails[i]));
                            }
                        }
                    }

                    // Handle the single instance.
                    else {
                        shippingItems.push(parseLineItem(lineItemDetails));
                    }
                }
            }

            return shippingItems;
        },
        getSubTotal: function(){
            return doesNodeExist(orderTotals) ? orderTotals.ItemSubtotal : "";
        },
        getShippingTotal: function(){
            return doesNodeExist(orderTotals) ? orderTotals.Shipping : "";
        },
        getTax: function(){
            return doesNodeExist(orderTotals) ? orderTotals.Tax : "";
        },
        getTaxInclusive: function(){
            return doesNodeExist(orderTotals) ? orderTotals.TaxInclusive : true;
        },
        getGrandTotal: function(){
            return doesNodeExist(orderTotals) ? orderTotals.GrandTotal : "";
        },
        getPaymentInfo: function(){
            if (payments === undefined) {
                payments = [];
                let paymentDetails = doesNodeExist(salesOrderDetail) ? salesOrderDetail.Payment : [];

                if (doesNodeExist(paymentDetails)) {
                    // Handle the array
                    if (paymentDetails.length !== undefined) {
                        for (let i = 0; i < paymentDetails.length; i++) {
                            payments[i] = parsePayment(paymentDetails[i]);
                        }
                    }

                    // Handle the single instance.
                    else {
                        payments[0] = parsePayment(paymentDetails);
                    }
                }
            }

            return payments;
        },
        getBuyerId: function(){
            return getShippingItem(shippingParty, "SHIP_TO", getShippingMemberID);
        },
        getShippingAddressName: function(){
            return getShippingItem(shippingParty, "SHIP_TO", getShipAddName);
        },
        getShippingAddressLine1: function(){
            return getShippingItem(shippingParty, "SHIP_TO", getShipAddLine1);
        },
        getShippingAddressLine2: function(){
            return getShippingItem(shippingParty, "SHIP_TO", getShipAddLine2);
        },
        getShippingAddressLine3: function(){
            return getShippingItem(shippingParty, "SHIP_TO", getShipAddLine3);
        },
        getShipAddLine3Japan: function() {
            return getShippingItem(shippingParty, "SHIP_TO", getShipAddLine3Japan);
        },
        getShippingAddressInstructions: function(){
            return getShippingItem(shippingParty, "SHIP_TO", getShipInstructions);
        },
        getShippingMethod: function(){
            return doesNodeExist(shippingMethod) ? unescapehtml.trustAsHtml(shippingMethod.Description) : "";
        },
        getShippingBusinessDays: function(){
            // var shippingMethodCode = doesNodeExist(shippingMethod) ? shippingMethod.Code : "";
            // todo get the number of business days given the shipping method code. What is the service for this?
            return "Need to wire";
        },
        getTrackingInfo: function(){
            if (tracking === undefined) {
                tracking = [];
                let trackingDetails = doesNodeExist(shipping) ? shipping.Tracking : [];

                if (doesNodeExist(trackingDetails)) {
                    // Handle the array
                    if (trackingDetails.length !== undefined) {
                        for (let i = 0; i < trackingDetails.length; i++) {
                            tracking[i] = parseTracking(trackingDetails[i]);
                        }
                    }

                    // Handle the single instance.
                    else {
                        tracking[0] = parseTracking(trackingDetails);
                    }
                }
            }

            return tracking;
        },
        getCustomShipMethodName: function() {
            let customShipMethodName = "";

            if (doesNodeExist(custom) && custom instanceof Array) {
                for (let i = 0; i < custom.length; i++) {
                    if (custom[i]["Type"] === "SHIP" && custom[i]["Key"] === "SENDER") {
                        customShipMethodName = custom[i]["Value"];
                        break;
                    }
                }
            }

            return unescapehtml.trustAsHtml(customShipMethodName);
        }
    };

    return orderDetailsObj;
}

function parseTracking(trackingDetails){
    let tracking = {
        getTrackingURL: function(){
            return doesNodeExist(trackingDetails.TrackURL) ? trackingDetails.TrackURL : "";
        },
        getTrackingNumber: function(){
            return doesNodeExist(trackingDetails.TrackNumber) ? trackingDetails.TrackNumber : "";
        }
    };
    return tracking;
}


function doesNodeExist(node){
    return node !== undefined && node !== null;
}

